import { useState, useEffect, useCallback } from 'react';
import * as serviceWorkerRegistration from '../serviceWorkerRegistration';

interface UseServiceWorkerUpdateReturn {
  showUpdatePrompt: boolean;
  updateAvailable: boolean;
  acceptUpdate: () => void;
  dismissUpdate: () => void;
}

export const useServiceWorkerUpdate = (): UseServiceWorkerUpdateReturn => {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);

  const acceptUpdate = useCallback(() => {
    if (waitingWorker) {
      // Send SKIP_WAITING message to the waiting service worker
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      setShowUpdatePrompt(false);
    }
  }, [waitingWorker]);

  const dismissUpdate = useCallback(() => {
    setShowUpdatePrompt(false);
  }, []);

  useEffect(() => {
    // Register service worker with custom callbacks
    serviceWorkerRegistration.register({
      onUpdate: (registration: ServiceWorkerRegistration) => {
        console.log('Service Worker update available');
        setUpdateAvailable(true);
        setShowUpdatePrompt(true);
        
        // Store reference to the waiting worker
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
        }
      },
      onSuccess: (registration: ServiceWorkerRegistration) => {
        console.log('Service Worker registered successfully');
      },
      onControllerChange: () => {
        // When the new service worker takes control, reload the page
        console.log('Service Worker controller changed, reloading page');
        window.location.reload();
      }
    });

    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // This will trigger a page reload when the new SW takes control
        window.location.reload();
      });
    }
  }, []);

  return {
    showUpdatePrompt,
    updateAvailable,
    acceptUpdate,
    dismissUpdate,
  };
};
